import LangDropdown from '../LangDropdown/LangDropdown';
import { useTranslation } from 'react-i18next';
import './Header.scss';
import { useAppSelector } from '../../hooks';
import { Link, useLocation, useNavigate } from 'react-router';
import HeadsetIcon from '@/icons/headset.svg?react';
import classNames from 'classnames';
import BackIcon from '@/icons/back.svg?react';
import Button from '../Button';
import Logo from '../Logo/Logo';
import {
  activeCompanySelector,
  userSelector,
} from '@/core/store/user/userSlice';
import { useBookMeetingModal } from '../BookMeetingModal/useBookMeetingModal';
import { PropsWithChildren } from 'react';
import { AuthedRoutesEnum, PricingModelEnum, RolesEnum } from '@/core/enums';
import AggregatorDropdown from '../AggregatorDropdown';
import { AvatarsChain } from '../AvatarsChain';
import { CompanyUser } from '@/core/types';
import Tooltip from '../Tooltip/Tooltip';

interface Props {
  btn?: JSX.Element;
  CTABtnLabel?: string;
  logo?: boolean;
  backBtn?: boolean;
  backBtnPath?: string;
  backBtnLabel?: string;
  className?: string;
  isShownBtn?: boolean;
  isCompaniesDropdownShown?: boolean;
  hideCompanyName?: boolean;
  inviteBtn?: JSX.Element;
  users?: CompanyUser[];
  endSlot?: JSX.Element;
}

const Header = ({
  btn,
  CTABtnLabel = 'Book consultation',
  logo = true,
  backBtn,
  backBtnPath,
  className = '',
  isShownBtn = true,
  backBtnLabel,
  children,
  isCompaniesDropdownShown,
  hideCompanyName,
  inviteBtn,
  users,
  endSlot,
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation();

  const user = useAppSelector(userSelector);
  const activeCompany = useAppSelector(activeCompanySelector);

  const navigate = useNavigate();

  const location = useLocation();

  const { openModal } = useBookMeetingModal();
  const handleOpenModal = () => openModal();

  const isEmbeddedPageShown =
    location.pathname === AuthedRoutesEnum.Profile ||
    location.pathname === AuthedRoutesEnum.Documents ||
    location.pathname === AuthedRoutesEnum.InvoiceBook ||
    location.pathname === AuthedRoutesEnum.COADebtor ||
    location.pathname === AuthedRoutesEnum.COARevenue ||
    location.pathname === AuthedRoutesEnum.COACountries ||
    location.pathname === AuthedRoutesEnum.COAShipments ||
    location.pathname === AuthedRoutesEnum.COAOther ||
    location.pathname === AuthedRoutesEnum.COAExpenses ||
    location.pathname.includes('distribution') ||
    location.pathname.includes('datev-transactions');

  return (
    <div
      className={classNames(
        'Header sticky left-0 top-0 z-10 border-b border-blue-200 px-4 py-2 lg:px-6  xl:px-14',
        className,
        { 'xl:!pl-6': isCompaniesDropdownShown }
      )}
      id='header'
    >
      <div
        className={classNames('flex items-center', {
          'justify-between': logo || backBtn || isCompaniesDropdownShown,
          'justify-end': !logo && !backBtn && !isCompaniesDropdownShown,
        })}
      >
        <div className='flex grow items-center'>
          {logo && (
            <Link to='/' tabIndex={-1} className='pt-1'>
              <Logo className='Header__logo' />
            </Link>
          )}
          {backBtn ? (
            !location.state?.hasPrevPath ? (
              <Link
                className='group flex items-center font-semibold text-dark-blue no-underline transition hover:text-ink'
                to={{
                  pathname: backBtnPath || '/',
                  search:
                    (location.state?.search
                      ? `${location.state.search}&`
                      : '') + `company_id=${activeCompany?.company_id}`,
                }}
                data-testid='back_button'
              >
                <BackIcon className='mr-2 [&>circle]:transition-[fill] group-hover:[&>circle]:fill-ink' />
                {t(backBtnLabel || 'Go to overview')}
              </Link>
            ) : (
              <Button
                data-testid='back_button'
                styling='naked'
                onClick={() => {
                  navigate(-1);
                }}
              >
                <BackIcon className='mr-2' />
                {t(backBtnLabel || 'Go to overview')}
              </Button>
            )
          ) : hideCompanyName ? null : (
            <div className='w-full'>
              {isCompaniesDropdownShown ? (
                <AggregatorDropdown className='max-w-[224px]' />
              ) : (
                activeCompany && (
                  <div
                    className='max-w-[450px] truncate font-bold'
                    data-testid='single-company-name'
                  >
                    {activeCompany.name}
                  </div>
                )
              )}
            </div>
          )}
        </div>
        {children}
        <div className='flex grow items-center justify-end'>
          {isShownBtn && (
            <div className='flex items-center gap-x-6 border-r pr-6'>
              {btn}
              {user &&
                ((user.Role !== RolesEnum.SELLER &&
                  user.Role !== RolesEnum.JTL_TAX_SETTINGS_CHECK) ||
                  activeCompany?.PricingModel === PricingModelEnum.Old ||
                  activeCompany?.PricingModel ===
                    PricingModelEnum.Professional) && (
                  <Tooltip content={t(CTABtnLabel)}>
                    <Button
                      styling='outline'
                      className='flex !h-8 !w-8 shrink-0 items-center justify-center !rounded-full !p-0'
                      onClick={handleOpenModal}
                      aria-label={t(CTABtnLabel)}
                      data-testid='book_consultation_button'
                    >
                      <HeadsetIcon className='h-6 w-6' />
                    </Button>
                  </Tooltip>
                )}
            </div>
          )}
          {endSlot}
          <div className='flex items-center pl-6'>
            {!isEmbeddedPageShown && (
              <>
                {user ? (
                  <AvatarsChain users={users || []} />
                ) : (
                  location.pathname !== '/' && (
                    <Link
                      to={'/'}
                      className='font-semibold text-dark-blue
              transition hover:text-ink'
                    >
                      {t('Login')}
                    </Link>
                  )
                )}
                {inviteBtn}
              </>
            )}
            <div className='ml-2 flex items-center'>
              <LangDropdown />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
